import { Injectable, signal, computed } from '@angular/core';
import { AuthService } from './auth.service';

// Permission constants
export const PERMISSIONS = {
    PATIENT_MODULE: 'PATIENT_MODULE',
    QUEUE_MODULE: 'QUEUE_MODULE',
    DOCTOR_MODULE: 'DOCTOR_MODULE',
    AUDIT_MODULE: 'AUDIT_MODULE',
    BROADCAST_MODULE: 'BROADCAST_MODULE'
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Role constants
export const ROLES = {
    ADMIN: 'ADMIN',
    PC_COORDINATOR: 'PC_COORDINATOR',
    PATIENT: 'PATIENT',
    DOCTOR: 'DOCTOR'
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

// Role to permission mapping
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
    [ROLES.ADMIN]: [
        PERMISSIONS.PATIENT_MODULE,
        PERMISSIONS.QUEUE_MODULE,
        PERMISSIONS.DOCTOR_MODULE,
        PERMISSIONS.AUDIT_MODULE,
        PERMISSIONS.BROADCAST_MODULE
    ],
    [ROLES.PC_COORDINATOR]: [
        PERMISSIONS.PATIENT_MODULE,
        PERMISSIONS.QUEUE_MODULE
    ],
    [ROLES.PATIENT]: [
        PERMISSIONS.PATIENT_MODULE
    ],
    [ROLES.DOCTOR]: [
        PERMISSIONS.DOCTOR_MODULE
    ]
};

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private _userPermissions = signal<Permission[]>([]);
    public userPermissions = this._userPermissions.asReadonly();

    constructor(private authService: AuthService) {
        // Subscribe to auth changes to update permissions
        // this.authService.currentUser$.subscribe(user => {
        //     if (user) {
        //         this._userPermissions.set(user.permissions as Permission[]);
        //     } else {
        //         this._userPermissions.set([]);
        //     }
        // });
    }

    hasPermission(permission: Permission): boolean {
        return this._userPermissions().includes(permission);
    }

    hasAnyPermission(permissions: Permission[], operator: 'AND' | 'OR' = 'OR'): boolean {
        const userPerms = this._userPermissions();
        
        if (operator === 'AND') {
            return permissions.every(p => userPerms.includes(p));
        }
        
        return permissions.some(p => userPerms.includes(p));
    }

    hasAllPermissions(permissions: Permission[]): boolean {
        return this.hasAnyPermission(permissions, 'AND');
    }

    getPermissionsForRole(role: Role): Permission[] {
        return ROLE_PERMISSIONS[role] || [];
    }

    // Check if user has access to a specific module
    canAccessModule(moduleName: string): boolean {
        const permission = `${moduleName}_MODULE` as Permission;
        return this.hasPermission(permission);
    }
}
